import React, { useState, useEffect, useRef, useContext } from 'react';
import './Payment.css';
import AppBar from '../../components/AppBar';
import Footer from '../../components/Footer';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { APP_CONFIG } from "../../config/appConfig";
import {
  getPlanPrice,
  getBlockchainPrice,
  getTotalPrice,
} from "../../utils/pricing";
import tracker from '../../utils/tracker';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTelegram } from '@fortawesome/free-brands-svg-icons';

import BitcoinIcon from '../../assets/bitcoin.png';
import EthereumIcon from '../../assets/ethereum.png';
import SolanaIcon from '../../assets/solana.png';
import TetherIcon from '../../assets/tether.png';
import LitecoinIcon from '../../assets/litecoin.png';
import BinanceIcon from '../../assets/binance.png';
import TronIcon from '../../assets/tron.png';

const plans = [
  { id: 'custom', name: 'Custom', duration: '3 months' },
  { id: 'pro', name: 'Pro', duration: 'Lifetime' },
  { id: 'ultimate', name: 'Ultimate', duration: 'Lifetime' },
];

const blockchains = [
  { id: 'bitcoin', name: 'Bitcoin', shortName: 'BTC', icon: BitcoinIcon },
  { id: 'ethereum', name: 'Ethereum', shortName: 'ETH', icon: EthereumIcon },
  { id: 'solana', name: 'Solana', shortName: 'SOL', icon: SolanaIcon },
  { id: 'tether', name: 'Tether USDT', shortName: 'USDT', icon: TetherIcon },
  { id: 'litecoin', name: 'Litecoin', shortName: 'LTC', icon: LitecoinIcon },
  { id: 'binance', name: 'Binance Coin', shortName: 'BNB', icon: BinanceIcon },
  { id: 'tron', name: 'Tron TRX', shortName: 'TRX', icon: TronIcon },
];

function Payment() {
  const location = useLocation();
  const planRowRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { user, loading: authLoading } = useContext(AuthContext);
  const [isMobile, setIsMobile] = useState(false);

  const initialPlan = location.state?.planId && plans.some(p => p.id === location.state.planId)
    ? location.state.planId
    : 'custom';

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  const handleSubscribe = async () => {
    if (authLoading) return;

    tracker.track('checkout_submitted', {
      planId: selectedPlan,
      chains: selectedBlockchains.length,
      totalPrice: getTotalPrice(selectedPlan, selectedBlockchains),
    });

    if (!user) {
      tracker.track('checkout_failed', { reason: 'not_logged_in' });
      setErrorMessage("You must be logged in to subscribe.");
      setShowErrorPopup(true);
      return;
    }

    try {
      setLoading(true);

      const referredBy = getCookie("affiliate_ref");

      const response = await fetch(`${APP_CONFIG.BASE_BACKEND_URL}/checkout/handle-invoice`, {
        method: "POST",
        credentials: "include",
        headers: { 
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          planId: selectedPlan,
          blockchains: selectedBlockchains,
          telegramUsername,
          referredBy: referredBy || null,
        })
      });

      const data = await response.json();

      if (!response.ok) {
        tracker.track('checkout_failed', { reason: (data.error || 'unknown_error').slice(0, 200) });
        setErrorMessage(data.error || "An error occurred");
        setShowErrorPopup(true);
        return;
      }

      window.location.href = data.invoiceUrl;

    } catch (err) {
      console.error(err);
      tracker.track('checkout_failed', { reason: 'network_error' });
      setErrorMessage("Failed to process request.");
      setShowErrorPopup(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePopupOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      setShowErrorPopup(false);
    }
  };

  const [selectedPlan, setSelectedPlan] = useState(initialPlan);
  const [selectedBlockchains, setSelectedBlockchains] = useState([]);
  const [blockchainWidth, setBlockchainWidth] = useState('auto');
  const [telegramUsername, setTelegramUsername] = useState('');

  useEffect(() => {
    if (planRowRef.current) {
      setBlockchainWidth(planRowRef.current.offsetWidth + 'px');
    }
  }, []);

  useEffect(() => {
    if (selectedPlan === 'ultimate') {
      setSelectedBlockchains(blockchains.map(b => b.id));
    } else {
      setSelectedBlockchains([]);
    }
  }, [selectedPlan]);

  const toggleBlockchain = (id) => {
    if (selectedPlan === 'ultimate') return;

    const alreadySelected = selectedBlockchains.includes(id);

    if (alreadySelected) {
      setSelectedBlockchains(prev => prev.filter(bid => bid !== id));
    } else {
      if (selectedPlan === 'pro') {
        if (selectedBlockchains.length >= 3) {
          setSelectedBlockchains(prev => {
            const newList = [...prev];
            newList.pop();
            return [...newList, id];
          });
        } else {
          setSelectedBlockchains(prev => [...prev, id]);
        }
      } else {
        setSelectedBlockchains(prev => [...prev, id]);
      }
    }
  };

  return (
    <div>
      <AppBar />
      <div style={{ height: '100px' }} />

      {showErrorPopup && (
        <div className="popup-overlay" onClick={handlePopupOverlayClick}>
          <div className="popup">
            <button className="popup-close" onClick={() => setShowErrorPopup(false)}>×</button>
            <p>{errorMessage}</p>
          </div>
        </div>
      )}

      <div className="payment-page">
        <div className="payment-plan-row" ref={planRowRef}>
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`payment-plan-box ${selectedPlan === plan.id ? 'payment-selected' : ''}`}
              onClick={() => {
                tracker.track('plan_selected', { planId: plan.id });
                setSelectedPlan(plan.id);
              }}
            >
              <div className="payment-select-circle">
                <div className={`payment-circle-inner ${selectedPlan === plan.id ? 'payment-filled' : ''}`} />
              </div>
              <div className="payment-plan-info">
                <div className="payment-plan-name">{plan.name} Plan</div>
                <div className="payment-plan-duration">{plan.duration} duration</div>
              </div>
            </div>
          ))}
        </div>

        <div className="payment-blockchain-selection" style={{ width: isMobile ? '100%' : blockchainWidth }}>
          {selectedPlan === 'custom' && <div className="payment-custom-note">+${APP_CONFIG.PRICES.blockchainUnitPrice} per selected blockchain</div>}
          <div className="payment-blockchain-header">
            <span className="payment-blockchain-title">Select Blockchains</span>
            {selectedPlan !== 'ultimate' && (
              <span className="payment-blockchain-count">
                {selectedBlockchains.length} out of {selectedPlan === 'pro' ? 3 : blockchains.length}
              </span>
            )}
          </div>

          <div className="payment-crypto-columns">
            {[0, 1].map((col) => (
              <div key={col} className="payment-crypto-column">
                {blockchains.slice(col * 4, col === 0 ? 4 : 7).map((crypto) => (
                  <div
                    key={crypto.id}
                    className="payment-crypto-item"
                    onClick={() => toggleBlockchain(crypto.id)}
                    style={{
                      opacity: selectedPlan === 'ultimate' || selectedBlockchains.includes(crypto.id) || selectedBlockchains.length < 3 || selectedPlan === 'custom'
                        ? 1
                        : 0.6,
                      cursor: 'pointer'
                    }}
                  >
                    <div className="payment-crypto-left">
                      <div className={`payment-crypto-select ${selectedBlockchains.includes(crypto.id) ? 'payment-crypto-select-active' : ''}`}>
                        <div className={`payment-crypto-dot ${selectedBlockchains.includes(crypto.id) ? 'payment-crypto-filled' : ''}`} />
                      </div>
                    </div>
                    <div className="payment-crypto-right">
                      <span className="payment-crypto-name">{crypto.shortName}</span>
                      <img src={crypto.icon} alt={crypto.name} className="payment-crypto-logo" />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="payment-pay-container" style={{ width: isMobile ? '100%' : blockchainWidth }}>
          <div className="payment-summary-left">
            <div className="summary-row">
              <span className="summary-label">{plans.find(p => p.id === selectedPlan)?.name} Plan</span>
              <span className="summary-value">
                ${getPlanPrice(selectedPlan)}
              </span>
            </div>

            <div className="summary-row">
              <span className="summary-label">Blockchains</span>
              <span className="summary-value">
                ${getBlockchainPrice(selectedPlan, selectedBlockchains)}
              </span>
            </div>

            <div className="summary-divider" />

            <div className="summary-row summary-subtotal-row">
              <span className="summary-subtotal-label">Subtotal</span>
              <span className="summary-subtotal-value">
                ${getTotalPrice(selectedPlan, selectedBlockchains)}
              </span>
            </div>

            <div className="summary-row">
              <span className="summary-label">Payment method</span>
              <span className="summary-value">Crypto</span>
            </div>
          </div>

          <div className="payment-summary-right">
            <div className="input-with-icon">
              <FontAwesomeIcon icon={faTelegram} className="input-icon" />
              <input
                type="text"
                className="telegram-input-field"
                placeholder="Telegram username"
                value={telegramUsername}
                onChange={(e) => setTelegramUsername(e.target.value)}
              />
            </div>

            <div className="summary-divider" />

            <button
              onClick={handleSubscribe}
              className="subscribe-button"
              disabled={
                !telegramUsername ||
                (selectedPlan === 'pro' && selectedBlockchains.length !== 3) ||
                (selectedPlan === 'custom' && selectedBlockchains.length === 0)
              }
            >
              Subscribe
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="payment-loading-overlay">
          <div className="payment-spinner"></div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default Payment;